/**
 * Frontier Model Pricing — $/Mtok input + output, context window, tier.
 *
 * DB-served; editable via Frontier Editor. Lab-RSS regex ingestor will
 * auto-update prices when labs announce changes (TODO).
 */
import { useEffect, useMemo, useState } from 'react'
import { DollarSign, RefreshCw, Filter } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface PricedModel {
  id: string
  model: string
  lab: string
  input_per_mtok: number
  output_per_mtok: number
  context_k: number
  tier: string
  released?: string
  notes?: string
}

const TIER_TONE: Record<string, string> = {
  'frontier-flagship':  'bg-rose/15 text-rose-light border-rose/30',
  'frontier':           'bg-purple/15 text-purple-light border-purple/30',
  'frontier-open':      'bg-emerald/15 text-emerald border-emerald/30',
  'reasoning':          'bg-amber/15 text-amber-light border-amber/30',
  'reasoning-small':    'bg-amber/10 text-amber-light border-amber/20',
  'general':            'bg-blue/15 text-blue-light border-blue/30',
  'small':              'bg-cyan/10 text-cyan border-cyan/20',
  'small-open':         'bg-emerald/10 text-emerald border-emerald/20',
}

export default function PricingPage() {
  const [models, setModels] = useState<PricedModel[]>([])
  const [labs, setLabs] = useState<string[]>([])
  const [tiers, setTiers] = useState<string[]>([])
  const [labFilter, setLabFilter] = useState('')
  const [tierFilter, setTierFilter] = useState('')
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    api.get('/api/frontier/pricing')
      .then(r => {
        setModels(r.data?.models || [])
        setLabs(r.data?.labs || [])
        setTiers(r.data?.tiers || [])
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  const filtered = useMemo(() =>
    models
      .filter(m => !labFilter || m.lab === labFilter)
      .filter(m => !tierFilter || m.tier === tierFilter),
  [models, labFilter, tierFilter])

  // Aggregate stats
  const cheapest = useMemo(() => {
    const blended = filtered.map(m => ({
      ...m,
      blended: m.input_per_mtok * 0.7 + m.output_per_mtok * 0.3,
    }))
    return blended.sort((a, b) => a.blended - b.blended)[0]
  }, [filtered])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <DollarSign size={18} className="text-emerald" /> FRONTIER MODEL PRICING
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          $ per million tokens · public list-prices · USD
        </span>
        <button type="button" onClick={load} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="card p-3">
          <div className="text-[10px] uppercase tracking-widest text-text-muted">Models tracked</div>
          <div className="text-2xl font-black font-mono mt-1 text-emerald">{models.length}</div>
        </div>
        <div className="card p-3">
          <div className="text-[10px] uppercase tracking-widest text-text-muted">Labs</div>
          <div className="text-2xl font-black font-mono mt-1 text-blue-light">{labs.length}</div>
        </div>
        <div className="card p-3">
          <div className="text-[10px] uppercase tracking-widest text-text-muted">Tiers</div>
          <div className="text-2xl font-black font-mono mt-1 text-amber-light">{tiers.length}</div>
        </div>
        <div className="card p-3">
          <div className="text-[10px] uppercase tracking-widest text-text-muted">Cheapest filtered (blended)</div>
          <div className="text-sm font-bold text-emerald mt-1 truncate">{cheapest?.model || '—'}</div>
          <div className="text-[10px] text-text-muted font-mono">
            ${cheapest ? (cheapest.input_per_mtok * 0.7 + cheapest.output_per_mtok * 0.3).toFixed(2) : '—'} blended
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 text-xs flex-wrap">
        <Filter size={12} className="text-text-muted" />
        <select className="input" value={labFilter} onChange={e => setLabFilter(e.target.value)}
          aria-label="Filter by lab">
          <option value="">All labs</option>
          {labs.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <select className="input" value={tierFilter} onChange={e => setTierFilter(e.target.value)}
          aria-label="Filter by tier">
          <option value="">All tiers</option>
          {tiers.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <span className="text-text-muted ml-auto">{filtered.length} of {models.length}</span>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-surface-raised">
            <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
              <th className="text-left px-3 py-2">Model</th>
              <th className="text-left px-3 py-2">Lab</th>
              <th className="text-left px-3 py-2">Tier</th>
              <th className="text-right px-3 py-2">Input $/Mtok</th>
              <th className="text-right px-3 py-2">Output $/Mtok</th>
              <th className="text-right px-3 py-2">Context</th>
              <th className="text-left px-3 py-2">Released</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map(m => (
              <tr key={m.id} className="hover:bg-surface-raised/40">
                <td className="px-3 py-1.5 font-semibold text-text-primary">{m.model}</td>
                <td className="px-3 py-1.5 text-purple-light">{m.lab}</td>
                <td className="px-3 py-1.5">
                  <span className={clsx('badge text-[9px] border', TIER_TONE[m.tier] || 'bg-surface-raised text-text-muted border-border')}>
                    {m.tier}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-right font-mono text-emerald">${m.input_per_mtok.toFixed(2)}</td>
                <td className="px-3 py-1.5 text-right font-mono text-amber-light">${m.output_per_mtok.toFixed(2)}</td>
                <td className="px-3 py-1.5 text-right font-mono text-blue-light">{m.context_k}k</td>
                <td className="px-3 py-1.5 font-mono text-text-muted">{m.released || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] text-text-muted text-center">
        Prices are public list-rates. Actual cost may vary by tier / region / volume / batch / cached prefix.
        Editable via /app/frontier-editor → Pricing.
      </p>
    </div>
  )
}
